import React, {
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  InputBase,
  Button,
  TableSortLabel,
  Box,
  Skeleton,
  Collapse,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@styles/all-images/icons/search.svg";
import ArrowDownIcon from "@styles/all-images/icons/chevron-down.svg";
import { visuallyHidden } from "@mui/utils";
import CustomPagination from "./CustomPagination";
import { toast } from "react-toastify";
import FilterIcon from "@styles/all-images/icons/rejectIcon.svg";
import ChevronDownIcon from "@styles/all-images/icons/chevron-down-grey.svg";
import CheckIcon from "@styles/all-images/icons/blue-tick.svg";
import ElipsedText from "@components/common/elipsis/ElipsedText";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import abortControllerHandler from "@/api/abortControllerHandler";
import debounce from "lodash/debounce";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 8,
  border: "1px solid #E9EAEB",
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
  marginBottom: theme.spacing(2),
}));

const StyledTableCell = styled(TableCell)(() => ({
  padding: "16px 24px",
  fontSize: "14px",
  "&.MuiTableCell-head": {
    backgroundColor: "#fff",
    fontWeight: 500,
    color: "#535862",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
}));

export interface TableColumn<T> {
  header: string;
  accessor: keyof T | ((row: T) => ReactNode);
  sortable?: boolean;
  sortKey?: string;
}

export interface Pagination {
  count: number;
  page: number;
  limit: number;
}
interface FilterOption {
  id: number;
  name: string;
}
export interface CustomTableProps<T> {
  columns: TableColumn<T>[];
  forceRefresh: boolean;
  setForceRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  forceReset?: boolean;
  setForceReset?: React.Dispatch<React.SetStateAction<boolean>>;
  hideControls?: boolean;
  removeInitialPadding?: boolean;
  hidePagination?: boolean;
  fetchData: (
    filters: any,
  ) => Promise<{ data: { data: T[]; meta: Pagination } }>;
  addButton?: {
    text: string;
    icon?: string;
    onClick: () => void;
  };
  secondaryAddButton?: {
    text: string;
    icon?: string;
    onClick: () => void;
  };
  filterButton?: {
    text: string;
    icon?: string;
    onClick: () => void;
  };
  archiveButton?: {
    text: string;
    icon?: string;
    onClick: () => void;
    isArchiveView: boolean;
  };
  filterOptions?: {
    show: boolean;
    options: FilterOption[];
    onFilterChange: (selected: FilterOption[]) => void;
    placeholder?: string;
  };
  customTableTitle?: string;
  noDataMessage?: string;
  noSearchDataMessage?: string;
  customPaginationDefaultValues?: {
    page?: number;
    limit?: number;
    search?: string;
    sortColumn?: string;
    sortDirection?: "asc" | "desc";
  };
  expandableRows?: boolean;
  renderExpandedRow?: (row: T) => React.ReactNode;
  isStatic?: boolean;
  data?: any;
}

export default function CustomTable<T>({
  columns,
  fetchData,
  addButton,
  secondaryAddButton,
  archiveButton,
  filterButton,
  forceRefresh,
  setForceRefresh,
  forceReset,
  setForceReset,
  hideControls,
  removeInitialPadding = false,
  hidePagination = false,
  filterOptions,
  customTableTitle,
  noDataMessage = "No data found",
  noSearchDataMessage = "No matching records found",
  customPaginationDefaultValues,
  expandableRows = false,
  renderExpandedRow,
  isStatic = false,
  data: staticData,
}: Omit<CustomTableProps<T>, "loading">) {
  const defaultValues = {
    page: 1,
    limit: 10,
    search: "",
    sortColumn: "",
    sortDirection: "asc" as "asc" | "desc",
  };
  const [searchTerm, setSearchTerm] = useState(
    customPaginationDefaultValues?.search || defaultValues.search,
  );
  const [page, setPage] = useState(
    customPaginationDefaultValues?.page || defaultValues.page,
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    customPaginationDefaultValues?.limit || defaultValues.limit,
  );
  const [totalPages, setTotalPages] = useState(1);
  const [orderBy, setOrderBy] = useState<string>(
    customPaginationDefaultValues?.sortColumn || defaultValues.sortColumn,
  );
  const [order, setOrder] = useState<"asc" | "desc">(
    customPaginationDefaultValues?.sortDirection || defaultValues.sortDirection,
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>({});
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const { abortCurrentRequest, resetAbortController } =
    abortControllerHandler();
  const debouncedSearchRef = useRef<any>(null);

  const debouncedSearch = useCallback(
    ({
      value,
      callback,
    }: {
      value?: string;
      callback: (value?: string) => void;
    }) => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }

      debouncedSearchRef.current = debounce((searchValue?: string) => {
        // abortCurrentRequest();
        callback(searchValue);
      }, 500);

      debouncedSearchRef.current(value);
    },
    [],
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, []);

  const resetTable = useCallback(() => {
    const isSame =
      page == defaultValues.page &&
      rowsPerPage == defaultValues.limit &&
      searchTerm == defaultValues.search;
    if (!isSame) {
      setPage(defaultValues.page);
      setRowsPerPage(defaultValues.limit);
      setSearchTerm(defaultValues.search);
      setOrderBy(defaultValues.sortColumn);
      setOrder(defaultValues.sortDirection);
    } else {
      setForceRefresh(true);
    }
  }, [
    defaultValues,
    setPage,
    setRowsPerPage,
    setSearchTerm,
    setOrderBy,
    setOrder,
  ]);

  useEffect(() => {
    if (forceReset) {
      resetTable();
      setForceReset?.(false);
    }
  }, [forceReset]);

  useEffect(() => {
    if (forceRefresh) {
      handleFetchData();
      setForceRefresh(false);
    }
  }, [forceRefresh]);

  useEffect(() => {
    resetAbortController();
    debouncedSearch({
      callback: () => {
        handleFetchData();
      },
    });
  }, [page, rowsPerPage, searchTerm, orderBy, order]);

  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await fetchData({
        page,
        limit: rowsPerPage,
        search: searchTerm,
        sortColumn: orderBy,
        sortDirection: order,
      });

      if (response.ok) {
        setData(response.data || {});
        setTotalPages(Math.ceil((response.data.meta.count || 1) / rowsPerPage));
      }
    } catch (error: any) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, orderBy, order, fetchData]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
    setPage(1);
    handleMenuClose();
  };

  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable || !column.sortKey) return;

    const isAsc = orderBy === column.sortKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column.sortKey);
  };

  const handleFilterSelect = (option: FilterOption | "all") => {
    let newSelected: FilterOption | null;

    if (option === "all") {
      newSelected = null;
    } else {
      const selectedOption = filterOptions?.options.find(
        (opt) => opt.id === option.id,
      );

      if (selectedOption) {
        const isSelected =
          selectedFilters.length === 1 &&
          selectedFilters[0].id === selectedOption.id;

        if (isSelected) {
          newSelected = null;
        } else {
          newSelected = selectedOption;
        }
      } else {
        newSelected = null;
      }
    }

    // Update the selected filters to only include the new selected option
    setSelectedFilters(newSelected ? [newSelected] : []); // Store as an array for consistency
    filterOptions?.onFilterChange(newSelected ? [newSelected] : []); // Pass as an array
    setIsFilterOpen(false);
  };

  const handleExpandRow = (rowIndex: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowIndex)) {
      newExpandedRows.delete(rowIndex);
    } else {
      newExpandedRows.add(rowIndex);
    }
    setExpandedRows(newExpandedRows);
  };

  const renderTableBody = useCallback(() => {
    if (loading) {
      return Array(rowsPerPage)
        .fill(0)
        .map((_, index) => (
          <StyledTableRow key={`skeleton-${index}`}>
            {expandableRows && (
              <StyledTableCell>
                <Skeleton animation="wave" height={24} width={24} />
              </StyledTableCell>
            )}
            {columns.map((_, colIndex) => (
              <StyledTableCell key={`skeleton-cell-${colIndex}`}>
                <Skeleton animation="wave" height={24} />
              </StyledTableCell>
            ))}
          </StyledTableRow>
        ));
    }

    const tableData = isStatic ? staticData : data.data;
    return (
      <>
        {tableData?.map?.((row: any, rowIndex: number) => (
          <React.Fragment key={rowIndex}>
            <StyledTableRow>
              {expandableRows && (
                <StyledTableCell>
                  <IconButton
                    aria-label="expand row"
                    size="small"
                    onClick={() => handleExpandRow(rowIndex)}
                  >
                    {expandedRows.has(rowIndex) ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                </StyledTableCell>
              )}
              {columns.map((column, colIndex) => (
                <StyledTableCell key={colIndex}>
                  {typeof column.accessor === "function"
                    ? column.accessor(row)
                    : String(row[column.accessor as keyof T])}
                </StyledTableCell>
              ))}
            </StyledTableRow>
            {expandableRows &&
              expandedRows.has(rowIndex) &&
              renderExpandedRow && (
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={columns.length + 1}
                  >
                    <Collapse
                      in={expandedRows.has(rowIndex)}
                      timeout="auto"
                      unmountOnExit
                    >
                      {renderExpandedRow(row)}
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
          </React.Fragment>
        ))}
        {!loading && (tableData?.length === 0 || !tableData) && (
          <StyledTableRow>
            <StyledTableCell
              colSpan={expandableRows ? columns.length + 1 : columns.length}
              className="tw-text-center tw-py-4"
            >
              {searchTerm ? noSearchDataMessage : noDataMessage}
            </StyledTableCell>
          </StyledTableRow>
        )}
      </>
    );
  }, [
    loading,
    data,
    columns,
    noDataMessage,
    searchTerm,
    noSearchDataMessage,
    expandableRows,
    expandedRows,
    renderExpandedRow,
    isStatic,
    staticData,
  ]);

  return (
    <div className={`${removeInitialPadding ? "" : "tw-p-6"}`}>
      <div
        className={`tw-flex tw-items-center tw-gap-2 tw-justify-between ${removeInitialPadding ? "" : "tw-mb-6"}`}
      >
        {customTableTitle && (
          <h1 className="tw-text-[22px] tw-font-semibold tw-text-[#232323]">
            {customTableTitle}
          </h1>
        )}
        {!hideControls && (
          <div
            className={`tw-flex tw-justify-between tw-items-center ${
              !customTableTitle ? "tw-w-full" : ""
            }`}
          >
            <div className="tw-flex tw-items-center tw-gap-4">
              <Paper
                component="form"
                sx={{
                  p: "2px 4px",
                  display: "flex",
                  alignItems: "center",
                  width: 256,
                  border: "1px solid #E9EAEB",
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
                <IconButton
                  type="button"
                  sx={{ p: "10px" }}
                  aria-label="search"
                >
                  <img
                    src={SearchIcon}
                    alt="Search"
                    className="tw-w-5 tw-h-5"
                  />
                </IconButton>
              </Paper>

              <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-text-sm tw-text-gray-600">
                  Rows per page
                </span>
                <Button
                  onClick={handleMenuOpen}
                  endIcon={
                    <img
                      src={ArrowDownIcon}
                      alt="Arrow Down"
                      className="tw-w-4 tw-h-4"
                    />
                  }
                  sx={{
                    border: "1px solid #E9EAEB",
                    color: "#535862",
                    textTransform: "none",
                    minWidth: "auto",
                    padding: "4px 12px",
                  }}
                >
                  {rowsPerPage}
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  {[5, 10, 15, 20, 25].map((value) => (
                    <MenuItem
                      key={value}
                      onClick={() => handleRowsPerPageChange(value)}
                    >
                      {value}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            </div>

            <div className="tw-flex tw-items-center tw-gap-4">
              {filterButton && (
                <button
                  onClick={filterButton.onClick}
                  className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 primary-custom-button-outline tw-border tw-border-[#146C94] font-primary"
                >
                  <img
                    src={filterButton.icon}
                    alt="Filter Icon"
                    className="tw-w-6 tw-h-6"
                  />
                  {filterButton.text}
                </button>
              )}
              {filterOptions?.show && !archiveButton?.isArchiveView && (
                <div className="tw-relative tw-w-[300px]">
                  <button
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm"
                  >
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <img
                        src={FilterIcon}
                        alt="Filter Icon"
                        className="tw-w-5 tw-h-5"
                      />
                      <span className="tw-text-[#717680]">
                        {selectedFilters.length ? (
                          <ElipsedText
                            maxLength={23}
                            text={`${selectedFilters?.[0]?.name} selected`}
                          />
                        ) : (
                          filterOptions.placeholder || "Select Filter"
                        )}
                      </span>
                    </div>
                    <img
                      src={ChevronDownIcon}
                      alt="Chevron Down"
                      className={`tw-w-5 tw-h-5 tw-transition-transform ${isFilterOpen ? "tw-rotate-180" : ""}`}
                    />
                  </button>

                  {isFilterOpen && (
                    <div className="tw-absolute tw-z-20 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-[#F5F5F5] tw-rounded-lg tw-shadow-lg">
                      <div className="">
                        <button
                          className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-2 hover:tw-bg-[#FAFAFA] tw-rounded"
                          onClick={() => handleFilterSelect("all")}
                        >
                          <span className="tw-text-[#717680]">Select All</span>
                          {selectedFilters.length === 0 && (
                            <img
                              src={CheckIcon}
                              alt="Check Icon"
                              className="tw-w-5 tw-h-5"
                            />
                          )}
                        </button>
                        {filterOptions.options.map((option) => (
                          <button
                            key={option.id}
                            className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-2 hover:tw-bg-[#FAFAFA] tw-rounded"
                            onClick={() => handleFilterSelect(option)}
                          >
                            <span className="tw-text-[#717680]">
                              <ElipsedText maxLength={28} text={option.name} />
                            </span>
                            {selectedFilters.some(
                              (selected) => selected.id === option.id,
                            ) && (
                              <img
                                src={CheckIcon}
                                alt="Check Icon"
                                className="tw-w-5 tw-h-5"
                              />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {archiveButton && (
                <button
                  onClick={archiveButton.onClick}
                  className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 primary-custom-button-outline font-primary"
                >
                  {archiveButton.icon && (
                    <img
                      src={archiveButton.icon}
                      alt="Archive Icon"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  )}
                  {archiveButton.text}
                </button>
              )}

              {addButton && !archiveButton?.isArchiveView && (
                <button
                  onClick={addButton.onClick}
                  className="tw-flex tw-items-center tw-gap-2 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
                >
                  {addButton.icon && (
                    <img
                      src={addButton.icon}
                      alt="Add Icon"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  )}
                  {addButton.text}
                </button>
              )}

              {secondaryAddButton && !archiveButton?.isArchiveView && (
                <button
                  onClick={secondaryAddButton.onClick}
                  className="tw-flex tw-items-center tw-gap-2 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
                >
                  {secondaryAddButton.icon && (
                    <img
                      src={secondaryAddButton.icon}
                      alt="Add Icon"
                      className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                    />
                  )}
                  {secondaryAddButton.text}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Table Component */}
      <StyledTableContainer>
        <Table className="tw-rounded-lg tw-border tw-border-[#E9EAEB]">
          <TableHead>
            <TableRow
              sx={
                isStatic
                  ? {
                      "& .MuiTableCell-head": {
                        backgroundColor: "#F2F2F7",
                      },
                    }
                  : {}
              }
            >
              {expandableRows && <StyledTableCell style={{ width: 50 }} />}
              {columns.map((column, index) => (
                <StyledTableCell
                  key={index}
                  onClick={() => column.sortable && handleSort(column)}
                  style={{ cursor: column.sortable ? "pointer" : "default" }}
                >
                  <div className="tw-flex tw-items-center tw-gap-1">
                    {column.header}
                    {column.sortable && column.sortKey === orderBy && (
                      <span>
                        {order === "asc" ? (
                          <ArrowUpwardIcon fontSize="small" />
                        ) : (
                          <ArrowDownwardIcon fontSize="small" />
                        )}
                      </span>
                    )}
                  </div>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </StyledTableContainer>

      {/* Pagination */}
      {!hidePagination && (
        <CustomPagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
        />
      )}
    </div>
  );
}
